import { type FC } from "react";
import {
  Box,
  Card,
  Checkbox,
  Chip,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import { useTranslation } from "react-i18next";
import type { CollectionsItem } from "@/partner/entities/contents";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";

const TITLE_MAX_LENGTH = 200;

function truncate(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

interface CollectionsListItemProps {
  collection: CollectionsItem;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onClickItem: (id: string) => void;
}

// Порт референсного CollectionListItem: чекбокс | превью | название+теги |
// счётчики детей (ссылки на списки с фильтром по коллекции) | публикация.
export const CollectionsListItem: FC<CollectionsListItemProps> = ({
  collection,
  selected,
  onSelectOneChange,
  onClickItem,
}) => {
  const { t } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const publishAt = collection.publishAt
    ? new Date(collection.publishAt)
    : null;

  // «Зайти внутрь» = список нужного типа с фильтром по этой коллекции
  // (референс: /videos?filter[collectionIds][]=id, breadcrumbs нет). Формат
  // массива — filter[...][]= (см. FilterProvider.changeParams).
  const videosUrl = `${PagePaths.videos}?filter[collectionIds][]=${collection.id}`;
  const collectionsUrl = `${PagePaths.collections}?filter[collectionIds][]=${collection.id}`;

  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 2,
        px: 1,
        cursor: "pointer",
        opacity: collection.publishAt ? 1 : 0.6,
        bgcolor: selected ? "background.selected" : "background.paper",
      }}
      onClick={() => onClickItem(collection.id)}
    >
      <Checkbox
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onSelectOneChange(collection.id, e)}
      />

      <Box
        sx={{
          position: "relative",
          width: { xs: 160 / 1.5, sm: 160 },
          height: { xs: 90 / 1.5, sm: 90 },
          flexShrink: 0,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {collection.preview?.url ? (
          <img
            src={String(collection.preview.url)}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <AllInboxIcon sx={{ color: "text.disabled", fontSize: 32 }} />
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <ItemTitleLink
          to={`${PagePaths.collections}?tab=edit&id=${collection.id}`}
          onOpen={() => onClickItem(collection.id)}
          underline="hover"
          variant="h6"
          color="text.primary"
          sx={{
            display: "block",
            fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
          }}
        >
          {truncate(collection.title, TITLE_MAX_LENGTH)}
        </ItemTitleLink>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ mt: 0.5, flexWrap: "wrap", alignItems: "center" }}
        >
          {collection.collectionType && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={collection.collectionType.title}
            />
          )}
          {!isMobile &&
            collection.tags.map((tag) => (
              <Chip
                key={tag.id}
                size="small"
                variant="outlined"
                label={tag.title}
              />
            ))}
        </Stack>
      </Box>

      {!isMobile && (
        <Box sx={{ flexShrink: 0, minWidth: 150 }}>
          <MuiLink
            component={RouterLink}
            to={videosUrl}
            variant="body2"
            color="text.primary"
            underline="hover"
            display="block"
            onClick={(e) => e.stopPropagation()}
          >
            {t("collections.item.childrenVideos", {
              count: collection.videosCount,
            })}
          </MuiLink>
          <MuiLink
            component={RouterLink}
            to={collectionsUrl}
            variant="body2"
            color="text.primary"
            underline="hover"
            display="block"
            onClick={(e) => e.stopPropagation()}
          >
            {t("collections.item.childrenCollections", {
              count: collection.collectionsCount,
            })}
          </MuiLink>
          <Typography variant="body2" color="text.secondary">
            {t("videos.item.viewsCount", { count: collection.totalViews })}
          </Typography>
        </Box>
      )}

      {!isMobile && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
          }}
        >
          {publishAt ? (
            <>
              <Box>
                <Typography variant="body2" color="text.primary" align="right">
                  {t("videos.item.publishedAtTime", {
                    publishAt: collection.publishAt,
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="right"
                >
                  {t("videos.item.publishedAtDate", {
                    publishAt: collection.publishAt,
                  })}
                </Typography>
              </Box>

              {collection.url && (
                <Tooltip title={t("videos.item.openTooltip")}>
                  <IconButton
                    target="_blank"
                    href={collection.url}
                    size="large"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.primary" align="right">
              {t("videos.item.notPublished")}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
};
